import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AlertService } from '../../../core/services/alert/alert.service';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { Observable, Subject, Subscription, debounceTime, filter } from 'rxjs';
import { SelectCountryPrefixComponent } from '../../component/select-country-prefix/select-country-prefix.component';

import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';

export function phoneNumberValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const phonePrefix = formGroup.get('phonePrefix')?.value;
    const phoneNumber = formGroup.get('phoneNumber')?.value;

    if (
      phonePrefix === '+34' &&
      (!phoneNumber || !/^[0-9]{9}$/.test(phoneNumber))
    ) {
      return { invalidPhoneNumber: true }; // Devuelve un error si no tiene 9 dígitos
    }

    return null; // Retorna nulo si todo está bien
  };
}

export function postalCodeValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const phonePrefix = formGroup.get('phonePrefix')?.value;
    const postalCode = formGroup.get('billing_postal_code')?.value;

    // Si es España (+34), el código postal debe tener exactamente 5 dígitos
    if (
      phonePrefix === '+34' &&
      (!postalCode || !/^[0-9]{5}$/.test(postalCode))
    ) {
      return { invalidSpanishPostalCode: true };
    }

    // Para otros países, mantener la validación original (4-5 dígitos)
    if (
      phonePrefix !== '+34' &&
      (!postalCode || !/^[0-9]{4,5}$/.test(postalCode))
    ) {
      return { invalidPostalCode: true };
    }

    return null;
  };
}

interface FileItem {
  file: File;
  selectOption: string;
  key: string;
}

@Component({
  selector: 'app-form-add-client',
  standalone: true,
  providers: [DatePipe],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    NgSelectModule,
    SelectCountryPrefixComponent
  ],

  templateUrl: './form-add-client.component.html',
  styleUrl: './form-add-client.component.css',
})
export class FormAddClientComponent implements OnInit, OnDestroy {
  @Output() backButtonClickAndEdit = new EventEmitter<string>();
  @Output() backButtonClick = new EventEmitter<boolean>();
  @Output() backButtonClick2 = new EventEmitter<boolean>();
  @Output() addClient = new EventEmitter<boolean>();

  @Input() optAdd = 'client';
  @Input() autoAssign = false;
  @Input() orderReference?: string; // ✅ Nuevo input para rastrear el pedido
  form: FormGroup;
  canUseVoip: boolean = ['Admin', 'Supervisor', 'Agent', 'Sales'].includes(localStorage.getItem('role') || '');

  clients: Array<{
    name: string;
    reference: string;
    tax_id: string;
    lastname: string;
  }> = [];
  filteredClients: Array<{ name: string; reference: string; tax_id: string }> =
    [];

  search$ = new Subject<string>();

  minDate: Date = new Date(1900, 0, 1);
  maxDate: Date = new Date();
  maxDateExpiration: Date = new Date(2040, 12, 12);

  step: number = 1;

  uploadFile = true;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('clientSelector') clientSelector: any;
  @Input() assigneeCreated = new EventEmitter<any>();
  private assigneeCreatedSubscription: Subscription | null = null;

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private clientService: ClientsService,
    private datePipe: DatePipe
  ) {
    this.form = this.fb.group(
      {
        status: [true],
        type: ['', Validators.required],
        tax_id: ['', Validators.required],
        tax_id_temp: [null],
        assignee_id: [null],
        name: ['', Validators.required],
        lastname: [''],
        email: ['', [Validators.required, Validators.email]],
        phone: [''],
        billing_address: ['', Validators.required],
        billing_postal_code: ['', [Validators.required]],
        billing_city: ['', Validators.required],
        billing_state: ['', Validators.required],
        phonePrefix: ['+34'],
        birth_date: [null],
        birthplace_country: ['spain'],
        nationality: [''],
        document_expiration_date: [null],
        document_issue_date: [null],
        phoneNumber: ['', Validators.required],
        driver_license_issue_date: [null],
      },
      { validators: [phoneNumberValidator(), postalCodeValidator()] }
    );

    this.form.get('type')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });

    this.form.get('phonePrefix')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.loadIndividualClients();

    this.assigneeCreatedSubscription =
      this.clientService.assigneeCreated$.subscribe((assignee) => {
        this.handleNewAssignee(assignee);
      });

    this.search$.pipe(debounceTime(200)).subscribe((term) => {
      this.fetchClient(term);
    });

    this.form.get('type')?.valueChanges.subscribe((typeValue) => {
      if (typeValue === 'organization') {
        this.form.get('lastname')?.reset();
        const assigneeIdControl = this.form.get('assignee_id');
        assigneeIdControl?.setValidators([
          Validators.required,
          this.nonEmptyValidator(),
        ]);
      }
    });

    this.form
      .get('tax_id')
      ?.valueChanges.pipe(
        debounceTime(200),
        filter((taxId) => !!taxId)
      )
      .subscribe((taxId) => {
        this.checkTaxId(taxId);
      });
  }

  ngOnDestroy(): void {
    if (this.assigneeCreatedSubscription) {
      this.assigneeCreatedSubscription.unsubscribe();
    }
  }

  taxIdExists = false;
  existingClient: any;

  showAlertFilter = false;
  showCountFilter = 0;
  showAlertFilterAll = false;
  existingClientReference: string = '';

  private nonEmptyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim();
      return value ? null : { nonEmpty: true };
    };
  }

  birthDateValidator(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const type = form.get('type')?.value;
      const birthDate = form.get('birth_date')?.value;

      if (type !== 'organization' && !birthDate) {
        return { birthDateRequired: true };
      }
      return null;
    };
  }

  isFieldInvalid(field: string): any {
    const control = this.form.get(field);
    return control?.invalid && control?.touched;
  }

  getFormError(field: string, errorType: string): boolean {
    const control = this.form.get(field);
    return control?.errors?.[errorType];
  }

  fetchClient(term: string): void {
    this.clientService.searchClients(term).subscribe({
      next: (response) => {
        this.alertService.close();
        if (response.success) {
          const clientData: any[] = response.data;
          this.clients = clientData.filter(
            (client) =>
              client.type === 'individual' || client.type === 'self-employed'
          );
          this.filteredClients = this.clients.map((client) => ({
            ...client,
            displayName: `${client.name} ${client.lastname || ''}`.trim(),
          }));
        } else {
          console.error('Error en la respuesta del servidor:', response);
        }
      },
      error: (error) =>
        console.error('Hubo un error al obtener los datos:', error),
    });
  }

  @Input() noAgregarFlag: boolean = false;
  onSubmit() {
    this.alertService.loading('swal_messages.creating_client');

    let formData = { ...this.form.value };
    formData.tax_id_temp = this.form.get('tax_id_temp')?.value;
    formData.tax_id = this.form.get('tax_id')?.value;

    let aux = this.form.get('phoneNumber')?.value;
    if (aux) {
      let phonePrefix = this.form.get('phonePrefix')?.value || '+34';
      const phoneNumber = this.form.get('phoneNumber')?.value;
      const combinedPhoneNumber = `(${phonePrefix})${phoneNumber}`;
      formData.phone = combinedPhoneNumber;
    }

    this.clientService.addData(formData).subscribe(
      (response) => {
        this.alertService.successOk('swal_messages.client_created');
        this.emitAddClient();
        //piñata
        if (this.optAdd === 'guarantee') {
          // ✅ Pasar orderReference junto con la garantía
          this.clientService.emitGuaranteeCreated({ 
            guarantee: response.data, 
            orderReference: this.orderReference 
          });
        } else {
          if (this.noAgregarFlag) {
            // ✅ Pasar orderReference junto con el cliente
            this.clientService.setClient({ 
              client: response.data, 
              orderReference: this.orderReference 
            });
          }
        }

        this.emitBackButtonClickAndEdit(response.data.reference);
        this.resetForm();
        this.fetchClient('');
        this.search$.next('');
        this.filteredClients = [...this.filteredClients];
        this.loadIndividualClients();
        this.reloadClientsList();
      },
      (error) => {
        this.alertService.error(error.error);
      }
    );
  }

  loadIndividualClients(): void {
    this.clientService.getData().subscribe({
      next: (response) => {
        this.alertService.close();
        if (response.success) {
          const clientData: any[] = response.data;
          this.clients = clientData.filter(
            (client) =>
              client.type === 'individual' || client.type === 'self-employed'
          );
          this.filteredClients = this.clients.map((client) => ({
            ...client,
            displayName: `${client.name} ${client.lastname || ''}`.trim(),
          }));
        } else {
          console.error('Error en la respuesta del servidor:', response);
        }
      },
      error: (error) =>
        console.error('Hubo un error al obtener los datos:', error),
    });
  }

  emitAddClient() {
    this.addClient.emit(true);
  }

  selectedTaxId: string = '';
  showAdditionalFields: boolean = false;
  isDuplicated: boolean = false;

  onNextStep(): void {
    this.selectedTaxId = this.form.get('tax_id')?.value || '';
    if (this.selectedTaxId) {
      this.form.get('tax_id');
      this.showAdditionalFields = true;
    } else {
      console.warn('El campo Tax ID está vacío');
    }
  }

  changeBirthplace(birthplace: string) {
    this.form.get('birthplace_country')?.setValue(birthplace);
  }

  changePrefix(event: any) {
    this.form.get('phonePrefix')?.setValue(event);
  }
  @Output() closeAssigneeCreateForm = new EventEmitter<void>();

  reloadClientsList(): void {
    this.fetchClient('');
    this.filteredClients = [...this.filteredClients];
  }

  @Input() isAssigneeMode = false;
  @Input() isEditMode = false;

  originalClientType: string | null = null;

  resetForm(): void {
    this.form.reset({
      status: true,
      type: '',
      tax_id: '',
      assignee_id: '',
      name: '',
      lastname: '',
      email: '',
      phone: '',
      billing_address: '',
      billing_postal_code: '',
      billing_city: '',
      billing_state: '',
      phoneNumber: '',
      phonePrefix: '+34',
      birthplace_country: 'spain',
      nationality: '',
    });
    this.step = 1;
    this.showAdditionalFields = false;
    this.taxIdExists = false;
    this.canProceed = false;
    this.files = [];
    this.originalClientType = null;
    this.isEditMode = false;
    this.changePrefix('+34');
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  emitBackButtonClickAndEdit(reference: string) {
    this.resetForm();
    this.backButtonClickAndEdit.emit(reference);
  }
  emitBackButtonClick() {
    this.resetForm();
    this.backButtonClick.emit(true);
  }

  emitBackButtonClick2() {
    if (this.isAssigneeMode) {
      this.isAssigneeMode = false;
      this.backButtonClick2.emit(true);
    }
  }
  getSelectedClientType(): string | null {
    return this.form.get('type')?.value || null;
  }

  canShowClientType(type: string): boolean {
    if (!this.isEditMode || !this.originalClientType) {
      return true; // En modo creación, mostrar todas las opciones
    }

    // Si el tipo original es organization, solo puede seguir siendo organization
    if (this.originalClientType === 'organization') {
      return type === 'organization';
    }

    // Si el tipo original es individual o self-employed, puede cambiar entre estos dos
    if (
      this.originalClientType === 'individual' ||
      this.originalClientType === 'self-employed'
    ) {
      return type === 'individual' || type === 'self-employed';
    }

    return true;
  }

  get phone() {
    return this.form.get('phone');
  }

  openCallPanel(): void {
    const prefix = this.form.get('phonePrefix')?.value || '';
    const number = this.form.get('phoneNumber')?.value || '';
    const phone = prefix + number;
    const name = (this.form.get('name')?.value || '') + ' ' + (this.form.get('lastname')?.value || '');
    if (phone) {
      window.dispatchEvent(new CustomEvent('softphone-call', {
        detail: { phone, name: name.trim() },
      }));
    }
  }

  nameValue: string | null = null;
  lastnameValue: string | null = null;
  individualClients: any[] = [];
  selectedClientType: string | null = null;

  searchClients(term: string): Observable<
    Array<{
      name: string;
      reference: string;
      tax_id: string;
      lastname?: string;
    }>
  > {
    return new Observable((observer) => {
      term = term || '';
      if (!term.trim()) {
        observer.next([]);
      } else {
        const filtered = this.clients.filter((client) => {
          const nameMatches = client.name
            ? client.name.toLowerCase().includes(term.toLowerCase())
            : false;
          const taxIdMatches = client.tax_id
            ? client.tax_id.toLowerCase().includes(term.toLowerCase())
            : false;
          const fullnameMatches = client.lastname
            ? `${client.name} ${client.lastname}`
                .toLowerCase()
                .includes(term.toLowerCase())
            : false;
          return nameMatches || taxIdMatches || fullnameMatches;
        });
        observer.next(filtered);
      }
    });
  }

  onSearch(event: any): void {
    const term = event.term || '';
    this.search$.next(term);
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  onPostalCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    const selectedDate = event.value;

    if (selectedDate) {
      const adjustedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      this.form.get('birth_date')?.setValue(adjustedDate);
    }
  }

  onDateChange2(event: MatDatepickerInputEvent<Date>) {
    const selectedDate = event.value;

    if (selectedDate) {
      const adjustedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      this.form.get('document_expiration_date')?.setValue(adjustedDate);
    }
  }

  onDateChange3(event: MatDatepickerInputEvent<Date>) {
    const selectedDate = event.value;

    if (selectedDate) {
      const adjustedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      this.form.get('driver_license_issue_date')?.setValue(adjustedDate);
    }
  }

  convertToLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  goToForm() {
    this.step = 2;
    this.showAdditionalFields = true;
    this.canProceed = true;
  }

  fillFormWithClientData(clientData: any): void {
    // Almacenar el tipo original del cliente
    this.originalClientType = clientData.type || null;
    this.isEditMode = true;

    if (clientData.tax_id?.startsWith('TEMP-')) {
      this.form.patchValue({
        tax_id_temp: clientData.tax_id,
        tax_id: '',
      });
    } else {
      this.form.patchValue({
        tax_id: clientData.tax_id,
        tax_id_temp: clientData.tax_id,
      });
    }
    const rawDate = clientData.birth_date;
    const fixedDate = rawDate ? new Date(rawDate.split(' ')[0]) : null; // "1966-01-22"
    const rawExpirationDate = clientData.document_expiration_date;
    const fixedExpirationDate = rawExpirationDate
      ? new Date(rawExpirationDate.split(' ')[0])
      : null; // "1966-01-22"
    this.form.patchValue({
      type: clientData.type || '',
      name: clientData.name || '',
      lastname: clientData.lastname || '',
      email: clientData.email || '',
      phone: clientData.phone || '',
      nationality: clientData.nationality || '',
      billing_address: clientData.billing_address || '',
      billing_postal_code: String(clientData.billing_postal_code || ''),
      billing_city: clientData.billing_city || '',
      billing_state: clientData.billing_state || '',
      phonePrefix: clientData.phonePrefix || '+34',
      phoneNumber: clientData.phoneNumber || '',
      document_issue_date: clientData.document_issue_date  || null,
      document_expiration_date: clientData.document_expiration_date
        ? fixedExpirationDate
        : null,
      birth_date: clientData.birth_date ? fixedDate : null,
    });
  }

  files: FileItem[] = [];
  uploadedFiles: File[] = [];

  selectOptionChanged(event: Event, index: number) {
    const selectElement = event.target as HTMLSelectElement;
    const option = selectElement.value;
    this.files[index].selectOption = option;
  }

  showFormEdit = false;

  @Output() clientDuplicateFound = new EventEmitter<string>();
  @Output() clientAutoAssign = new EventEmitter<string>();
  onDuplicateFound() {
    if (this.autoAssign) {
      this.clientAutoAssign.emit(this.existingClientReference);
    }

    if (this.existingClientReference) {
      this.clientDuplicateFound.emit(this.existingClientReference);
    }
    this.resetForm();
  }
  /*******************/

  isDragOver = false;
  isUploading = false;
  canProceed = false;
  uploadComplete = false;
  isCheckingTaxId = false;

  getThumbnailSrc(file: File): string {
    const patterns = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      pdf: 'application/pdf',
      doc: 'application/msword',
      csv: 'text/csv',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xslx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'word-openxml':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      'xls-openxml':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    for (const [key, mimeType] of Object.entries(patterns)) {
      if (file.type === mimeType) {
        switch (key) {
          case 'jpg':
          case 'jpeg':
          case 'png':
            return `../../../assets/icons/jpg.svg`;
            break;
          case 'pdf':
            return `../../../assets/icons/pdf.svg`;
            break;
          case 'word-openxml':
          case 'doc':
          case 'docx':
            return `../../../assets/icons/doc.svg`;
            break;
          case 'xls-openxml':
          case 'xls':
            return `../../../assets/icons/xls.svg`;
            break;
          default:
            return '../../../assets/icons/file.svg';
            break;
        }
      }
    }
    return '../../../assets/icons/file.svg';
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.canProceed = this.files.length > 0;

    if (this.files.length === 0) {
      this.resetForm();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  addFiles(files: FileList): void {
    for (const file of Array.from(files)) {
      if (
        !this.files.some(
          (f) => f.file.name === file.name && f.file.size === file.size
        )
      ) {
        const newFileItem: FileItem = {
          file,
          selectOption: 'defaultOption',
          key: 'defaultOption',
        };
        this.files.push(newFileItem);
      }
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles) {
      this.handleFilesUpload(droppedFiles);
    }
  }

  checkTaxId(taxId: string): void {
    const normalizedTaxId = taxId.toUpperCase();
    this.isCheckingTaxId = true;

    this.clientService.checkTaxId(normalizedTaxId).subscribe({
      next: (response) => {
        if (response.success && response.data?.reference) {
          this.taxIdExists = true;
          this.showAlertFilter = true;
          this.canProceed = true;
          this.existingClientReference = response.data.reference;
        } else {
          this.resetDuplicateStatus();
        }
      },
      error: (error) => {
        console.error('Error al verificar el Tax ID:', error);
        this.resetDuplicateStatus();
      },
      complete: () => {
        this.isCheckingTaxId = false;
        this.updateCanProceed();
      },
    });
  }

  resetDuplicateStatus(): void {
    this.taxIdExists = false;
    this.showAlertFilter = false;
    this.isDuplicated = false;
    this.existingClient = null;
    this.existingClientReference = '';
  }

  onTaxIdChange(): void {
    const taxIdControl = this.form.get('tax_id');
    this.isCheckingTaxId = false;
    if (taxIdControl) {
      const upperTaxId = taxIdControl.value.toUpperCase();
      taxIdControl.setValue(upperTaxId, { emitEvent: false });
      this.checkTaxId(upperTaxId);
    }

    this.canProceed = true;
  }

  updateCanProceed(): void {
    this.canProceed = true;
    /*  this.canProceed =
       (!this.taxIdExists && !this.isCheckingTaxId) || this.uploadComplete; */
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files) {
      this.handleFilesUpload(files);
    }
  }

  handleFilesUpload(files: FileList): void {
    if (files && files.length > 0) {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('file[]', file));

      this.alertService.loading('swal_messages.please_wait');

      this.clientService.uploadDocument(formData).subscribe({
        next: (response) => {
          this.addFiles(files);
          this.alertService.close();
          const clientData = response.data;
          if (clientData.tax_id) {
            this.form.patchValue(
              { tax_id: clientData.tax_id },
              { emitEvent: true }
            );
            this.fillFormWithClientData(clientData);
            this.showAdditionalFields = true;
          }
          this.uploadComplete = true;
          this.isUploading = false;
          this.updateCanProceed();
        },
        error: (error) => {
          console.error('Error al subir el documento:', error);
          this.alertService.error(error.error);
          this.fileInput.nativeElement.value = '';
        },
      });
    }
  }

  onBirthplaceCountrySelected(selectedCountryName: string) {
    this.form.get('birthplace_country')?.setValue(selectedCountryName);
  }

  onPhonePrefixSelected(selectedPrefix: string) {
    this.form.get('phone_prefix')?.setValue(selectedPrefix);
  }

  @Input() document_buttons_chico = false;
  @Output() emitOpenAssigneeCreateForm = new EventEmitter<boolean>();

  openAssigneeCreateForm() {
    this.document_buttons_chico = true;
    this.emitOpenAssigneeCreateForm.emit();
  }
  closeAssigneeForm(): void {
    this.isAssigneeMode = false;
  }

  /***************** */

  showAssigneeForm = false;

  handleNewAssignee(newAssignee: any) {
    if (newAssignee.success && newAssignee.data) {
      const newClient = {
        reference: newAssignee.data.reference,
        displayName: `${newAssignee.data.name} ${
          newAssignee.data.lastname || ''
        }`.trim(),
        name: newAssignee.data.name,
        lastname: newAssignee.data.lastname,
        tax_id: newAssignee.data.tax_id,
        type: newAssignee.data.type,
      };

      this.filteredClients = [...this.filteredClients, newClient];
      this.form.get('assignee_id')?.setValue(newAssignee.data.reference);
      this.reloadClientsList();
    }
  }
}
